import React, { useCallback, useState, useEffect } from 'react';
import { useGame } from '../context/GameContext.jsx';
import toastTranslations from '../../data/toast.json';

const Card = ({ card, isMissionCard }) => {
  const { translations, lang, getCardImagePath, formatFieldValue, handleHypothesisChange, showToastMessage } = useGame();

  const [isBookmarked, setIsBookmarked] = useState(() => localStorage.getItem(`hypothesis_${card.CardId}`) === 'true');

  useEffect(() => {
    setIsBookmarked(localStorage.getItem(`hypothesis_${card.CardId}`) === 'true');
  }, [card.CardId]);

  const onBookmarkChange = useCallback((e) => {
    const checked = e.target.checked;
    setIsBookmarked(checked);
    handleHypothesisChange(card.CardId, checked);
  }, [card.CardId, handleHypothesisChange]);

  const cardImageUrl = getCardImagePath(card);

  const handleModifierClick = useCallback((modifierKey) => {
    const toastKey = toastTranslations[modifierKey];
    if (toastKey) {
      const message = translations[lang]?.[toastKey];
      if (message) {
        showToastMessage(message);
      } else {
        console.warn(`Translation key '${toastKey}' not found for language '${lang}'.`);
      }
    } else {
      console.warn(`Modifier key '${modifierKey}' not found in toast.json.`);
    }
  }, [translations, lang, showToastMessage]);


  let cardOverlayBadgeHtml = null;
  if (card.Type && card.Type.toLowerCase() === 'clue') {
    let overlayBadgeClass = '';
    switch (card.Category?.toLowerCase()) {
      case 'location': overlayBadgeClass = 'bg-success'; break;
      case 'person': overlayBadgeClass = 'bg-danger'; break;
      case 'evidence': overlayBadgeClass = 'bg-info'; break;
      default: break; 
    }
    const cardNumber = card.CardId?.substring(1);
    cardOverlayBadgeHtml = (
      <span className={`badge ${overlayBadgeClass} card-overlay-badge`}>
        {cardNumber}
      </span>
    );
  }

  let hypothesisCheckboxHtml = null;
  if (card.Type && card.Type.toLowerCase() === 'clue') { 
    const bookmarkIconClass = isBookmarked ? 'fa-solid fa-bookmark' : 'fa-regular fa-bookmark';

    hypothesisCheckboxHtml = (
      <div className="hypothesis-bookmark-container">
          <input
            type="checkbox"
            className="btn-check"
            id={`hypothesisCheck-${card.CardId}`}
            autoComplete="off"
            data-card-code={card.CardId}
            checked={isBookmarked}
            onChange={onBookmarkChange}
            />
          <label className="btn btn-outline-warning" htmlFor={`hypothesisCheck-${card.CardId}`}>
            <i className={`${bookmarkIconClass} fs-4`}></i>
          </label>
      </div>
    );
  }

  let costHtml = null;
  if (typeof card.Cost === 'number') { 
    const costValue = card.Cost;
    const costBadgeClass = costValue >= 0 ? 'bg-success' : 'bg-danger';
    const costSign = costValue >= 0 ? '+' : '';

    costHtml = (
      <span className={`badge ${costBadgeClass} mission-screen-badge`}>
        <i className="fa-regular fa-hourglass-half me-1"></i> {costSign}{costValue}
      </span>
    );
  }

  const modifiersArray = card.ModifiersLabel ? card.ModifiersLabel.split(';').map(m => m.trim()).filter(m => m !== '') : [];
  const goalsArray = card.GoalsLabel ? card.GoalsLabel.split(';').map(g => g.trim()).filter(g => g !== '') : [];

  const modifierItems = card.Modifiers ? card.Modifiers.map((modifierKey, index) => {
    const displayLabel = modifiersArray[index] || modifierKey;
    return (
      <li key={modifierKey} className="list-inline-item">
        <span 
          className="badge bg-warning text-dark mission-screen-badge clickable-investigation-badge"
          onClick={() => handleModifierClick(modifierKey)}
        >
          {displayLabel}
        </span>
      </li>
    );
  }) : [];
  const hasModifiers = modifierItems.length > 0;
  const modifiersHtml = (card.Type && card.Type.toLowerCase() === 'mission' && hasModifiers) ? (
    <div className="text-center mt-4">
      <h5 className="mission-section-title">
        <span className="fw-bold text-white">{translations[lang]?.modifiers_label_full}</span> 
        <ul className="list-inline mt-2 mb-0">
          {modifierItems}
        </ul>
      </h5>
    </div>
  ) : null;


  const goalItems = card.Goals ? card.Goals.map((goalKey, idx) => {
    const displayLabel = goalsArray[idx] || goalKey;
    return (
      <li key={goalKey} className="list-inline-item">
        <span 
          className="badge bg-success mission-screen-badge clickable-investigation-badge"
          onClick={() => handleModifierClick(goalKey)}
        >
          {displayLabel}
        </span>
      </li>
    );
  }) : [];
  const hasGoals = goalItems.length > 0;
  const goalsHtml = (card.Type && card.Type.toLowerCase() === 'mission' && hasGoals) ? (
    <div className="text-center mt-3">
      <h5 className="mission-section-title">
        <span className="fw-bold text-white">{translations[lang]?.goals_label_full}</span> 
        <ul className="list-inline mt-2 mb-0">
          {goalItems}
        </ul>
      </h5>
    </div>
  ) : null;

  const processedFieldValue = formatFieldValue(card.FieldValue);

  return (
    <>
      <div className="carousel-caption-custom d-block">
        <h2 className="card-title">{cardOverlayBadgeHtml}{card.Title || (translations[lang]?.default_card_title || 'Default Title')}</h2>
        <h4 className="card-subtitle mb-2">{card.Subtitle || ''}</h4>
        <p className="mb-3">
          <span className="badge bg-info mission-screen-badge">{card.CategoryLabel || ''}</span>{' '} 
          {card.SubType && <span className="badge bg-secondary ms-2 mission-screen-badge">{card.SubType}</span>}
          {costHtml && <span className="ms-2">{costHtml}</span>} 
        </p>
      </div>

      <div className="card-display">
        <div className="card-image-wrapper">
          <img src={cardImageUrl} alt={translations[lang]?.mission_card_alt || "Card Image"} className="card-image" />
        </div>
        {card.FieldType === 'text' && processedFieldValue && (
          <p dangerouslySetInnerHTML={{ __html: processedFieldValue }}></p>
        )}
      </div>
      {hypothesisCheckboxHtml}

      {modifiersHtml} 
      {goalsHtml} 
    </>
  );
};

export default Card;
